import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertTriangle } from "lucide-react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

type Accion = {
  cargo_id: number
  cargo: string
  padre_cargo: string | null
  persona_id: number | null
  ocupante: string | null
  avisos: string[]
}
type Propuesta = { acciones: Accion[]; creadas: number }

/**
 * «Completar desde cargos»: las posiciones que faltan, sacadas del «reporta a» de cada
 * cargo.
 *
 * ⚠ **Primero se enseña lo que va a pasar, y sólo después se aplica.** La propuesta
 * dice qué cargo cuelga de cuál, quién lo ocupa y por qué una posición queda vacante o
 * arriba. Aplicar a ciegas una docena de cajas sería obligar a deshacerlas a mano.
 *
 * ⚠ **Sólo añade.** Lo que ya está en el mapa —colocado a mano, con sus líneas
 * punteadas y sus notas— no se toca; por eso repetirlo no crea nada.
 */
export function CompletarDesdeCargos({
  abierto,
  organigramaId,
  onCerrar,
}: {
  abierto: boolean
  organigramaId: number
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const propuesta = useQuery({
    queryKey: ["organigrama", "propuesta", organigramaId],
    queryFn: () =>
      api.get<Propuesta>(`/api/v1/organigramas/${organigramaId}/propuesta-desde-cargos`),
    enabled: abierto,
    // Siempre la de ahora: entre dos aperturas alguien pudo añadir cargos.
    gcTime: 0,
  })

  const aplicar = useMutation({
    mutationFn: () =>
      api.post<Propuesta>(`/api/v1/organigramas/${organigramaId}/completar-desde-cargos`),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["organigrama", "nodos", organigramaId] })
      clienteQuery.invalidateQueries({ queryKey: ["organigrama", "propuesta", organigramaId] })
      onCerrar()
    },
    meta: { exito: "Posiciones añadidas al organigrama" },
  })

  const acciones = propuesta.data?.acciones ?? []

  return (
    <Modal
      abierto={abierto}
      titulo="Completar desde los cargos"
      descripcion="Una posición por cada cargo que todavía no tiene la suya, colgada del cargo al que reporta. Lo que ya está no se toca."
      onCerrar={onCerrar}
    >
      {propuesta.isPending && (
        <p role="status" className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Calculando…
        </p>
      )}
      {propuesta.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {propuesta.error instanceof ErrorApi
            ? propuesta.error.message
            : "No se pudo calcular la propuesta"}
        </p>
      )}
      {propuesta.isSuccess && acciones.length === 0 && (
        <p className="text-sm">
          No falta ninguna posición: cada cargo de este mapa ya tiene la suya.
        </p>
      )}
      {acciones.length > 0 && (
        <ul className="max-h-[55vh] space-y-2 overflow-y-auto" aria-label="Posiciones a crear">
          {acciones.map((a) => (
            <li key={a.cargo_id} className="superficie p-3 text-sm">
              <p className="font-medium">{a.cargo}</p>
              <p style={{ color: "var(--fg-muted)" }}>
                {a.padre_cargo === null
                  ? "Arriba, sin jefe en este mapa"
                  : `Cuelga de ${a.padre_cargo}`}
                {" · "}
                {a.ocupante ?? "Vacante"}
              </p>
              {a.avisos.map((aviso) => (
                <p
                  key={aviso}
                  className="mt-1 flex items-start gap-1.5 text-xs"
                  style={{ color: "var(--warning)" }}
                >
                  <AlertTriangle size={14} aria-hidden={true} className="mt-px shrink-0" />
                  {aviso}
                </p>
              ))}
            </li>
          ))}
        </ul>
      )}
      {aplicar.error && (
        <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
          {aplicar.error instanceof ErrorApi ? aplicar.error.message : "No se pudo completar"}
        </p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Boton variante="fantasma" onClick={onCerrar}>
          {acciones.length === 0 ? "Cerrar" : "Cancelar"}
        </Boton>
        {acciones.length > 0 && (
          <Boton variante="primario" disabled={aplicar.isPending} onClick={() => aplicar.mutate()}>
            Crear {acciones.length} posici{acciones.length === 1 ? "ón" : "ones"}
          </Boton>
        )}
      </div>
    </Modal>
  )
}
